import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { cx } from "@/lib/cx";

type Common = {
  children?: ReactNode;
  href?: string;
  /** Required — becomes both aria-label and title. */
  label: string;
  size?: number;
  className?: string;
};

export type IconTileProps = Common &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement> & ButtonHTMLAttributes<HTMLButtonElement>, keyof Common>;

/** Square inset tile at radius-md. Hovers to white fill with an ink border. */
export function IconTile({ children, href, label, size = 36, className, ...rest }: IconTileProps) {
  const classes = cx(
    "inline-flex items-center justify-center cursor-pointer no-underline",
    "border border-border-hairline rounded-md bg-surface-inset text-text-body",
    "transition-[background-color,border-color,color] duration-160 ease-out",
    "hover:border-text-primary hover:bg-surface-card hover:text-text-primary",
    className,
  );
  const style = { width: size, height: size };

  if (href) {
    return (
      <a
        href={href}
        aria-label={label}
        title={label}
        target="_blank"
        rel="noopener noreferrer"
        className={classes}
        style={style}
        {...rest}
      >
        {children}
      </a>
    );
  }

  return (
    <button type="button" aria-label={label} title={label} className={classes} style={style} {...rest}>
      {children}
    </button>
  );
}
